// The visual email builder: block palette (left), the email itself as a live canvas (centre) and the
// settings panel (right). Drag blocks in or click to add; drag to reorder; click any block to edit it
// — text is edited right on the canvas. Drop image files straight onto the email to upload them.
// The canvas uses the same renderer as the sent email, so what you see is what clients get.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BLOCKS, GROUPS, RICH_INLINE, PLACEHOLDER, makeBlock, cloneBlock } from './blocks.jsx';
import { Editable, Toolbar } from './RichText.jsx';
import { BlockInspector, ThemePanel } from './Inspector.jsx';
import { uploadImageFile } from './ImagePicker.jsx';
import { fetchTemplates, saveTemplate, deleteTemplate, saveBrandTheme } from '../../api/marketing.js';
import { resolveTheme, blockRow, blockInner, blockPadding, headerHtml, renderCampaign, FONTS, MERGE_FIELDS, blockRich, textToRich } from '../../lib/emailRender.js';

const errMsg = (e) => e?.response?.data?.error?.message || e?.message || 'Something went wrong.';
const NEW = 'text/x-t8-new';
const MOVE = 'text/x-t8-move';
const SECTION = 'text/x-t8-section';
// On the canvas, personalisation shows as the field itself ({firstName}); Preview shows real values.
const TAG_VARS = Object.fromEntries(MERGE_FIELDS.map((f) => [f, `{${f}}`]));
const isTyping = (el) => el && (el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName));

/** Inline-edit styles for a rich block, matching how the renderer draws it. */
function editStyle(b, T) {
  const font = (k) => (FONTS[k] || FONTS.system).stack;
  if (b.type === 'heading') {
    const level = Number(b.level) || 1;
    return { fontFamily: font(T.headingFont), fontSize: b.size || { 1: 26, 2: 21, 3: 17 }[level], lineHeight: 1.25, fontWeight: 700, color: b.color || T.heading, textAlign: b.s?.align || 'left', letterSpacing: '-0.01em' };
  }
  if (b.type === 'quote') return { fontFamily: font(T.font), fontSize: 17, lineHeight: 1.55, color: T.heading, borderLeft: `3px solid ${b.color || T.accent}`, paddingLeft: 18 };
  return { fontFamily: font(T.font), fontSize: b.size || T.fontSize, lineHeight: 1.65, color: b.color || T.text, textAlign: b.s?.align || 'left' };
}

function CanvasBlock({ b, i, T, ctx, selected, onSelect, onEditHtml, onDragOverIdx, onDragDone, actions, caret }) {
  const ref = useRef(null);
  const editorRef = useRef(null);
  const inline = selected && RICH_INLINE.has(b.type);
  const shown = { ...b, s: { ...(b.s || {}), hide: undefined } };
  const inner = useMemo(() => blockInner(shown, ctx, TAG_VARS, T), [b, ctx, T]); // eslint-disable-line react-hooks/exhaustive-deps
  const html = useMemo(() => (inner ? `<table role="presentation" width="100%" cellpadding="0" cellspacing="0">${blockRow(shown, ctx, TAG_VARS, T)}</table>` : ''), [inner]); // eslint-disable-line react-hooks/exhaustive-deps
  const pad = blockPadding(b);
  const badges = [b.s?.hide === 'mobile' && 'Desktop only', b.s?.hide === 'desktop' && 'Phone only', b.only?.currencies?.length && `Only ${b.only.currencies.join(', ')}`].filter(Boolean);

  return (
    <div
      ref={ref}
      data-block={b.id}
      onMouseDown={(e) => {
        if (selected) return;
        // Selecting swaps the block for its editor under the pointer; without preventDefault the
        // browser's own mousedown handling would then move focus away from that editor.
        if (RICH_INLINE.has(b.type)) e.preventDefault();
        onSelect(b.id, { x: e.clientX, y: e.clientY });
      }}
      onClick={(e) => {
        if (e.target.closest('a') && !inline) e.preventDefault(); // links don't navigate while editing
        e.stopPropagation();
      }}
      onDragOver={(e) => {
        e.preventDefault();
        e.stopPropagation();
        const r = ref.current.getBoundingClientRect();
        onDragOverIdx(e.clientY < r.top + r.height / 2 ? i : i + 1);
      }}
      className={`group relative ${selected ? 'z-10 outline outline-2 outline-accent' : 'hover:outline hover:outline-1 hover:outline-dashed hover:outline-accent/60'}`}
      style={{ outlineOffset: -1 }}
    >
      {inline ? (
        <div style={{ padding: `${pad.pt}px ${pad.px}px ${pad.pb}px`, background: b.s?.bg || undefined, '--t8-link': T.link }}>
          <div className="absolute -top-10 left-0 z-30 rounded-md border border-line bg-surface px-1 py-0.5 shadow-md">
            <Toolbar editorRef={editorRef} compact={b.type === 'heading'} />
          </div>
          <Editable
            editorRef={editorRef}
            html={blockRich(b)}
            onChange={onEditHtml}
            autoFocus
            caretAt={caret}
            placeholder={PLACEHOLDER[b.type]}
            style={editStyle(b, T)}
            className={b.type === 'heading' ? 't8-editable-heading' : ''}
          />
        </div>
      ) : inner ? (
        <div className="t8-canvas-block" dangerouslySetInnerHTML={{ __html: html }} />
      ) : (
        <div style={{ padding: `${Math.max(pad.pt, 8)}px ${pad.px}px ${Math.max(pad.pb, 8)}px` }}>
          <div className="flex items-center justify-center gap-2 rounded-md border-2 border-dashed border-line-strong bg-surface-raised/60 px-3 py-5 text-center text-sm text-text-muted">
            <span className="opacity-70">{BLOCKS[b.type]?.icon}</span> {PLACEHOLDER[b.type] || BLOCKS[b.type]?.label}
          </div>
        </div>
      )}

      {/* label + actions */}
      <div className={`pointer-events-none absolute left-0 top-0 flex -translate-y-full gap-1 ${selected && !inline ? 'flex' : 'hidden group-hover:flex'} ${inline ? '!hidden' : ''}`}>
        <span className="rounded-t bg-accent px-1.5 py-0.5 text-[10px] font-medium text-white">{BLOCKS[b.type]?.label}</span>
      </div>
      {badges.length > 0 && (
        <div className="pointer-events-none absolute right-1 top-1 flex gap-1">
          {badges.map((x) => (
            <span key={x} className="rounded bg-info-light px-1.5 py-0.5 text-[10px] font-medium text-info shadow-sm">{x}</span>
          ))}
        </div>
      )}
      {selected && (
        <div className="absolute -right-11 top-0 flex flex-col gap-0.5 rounded-md border border-line bg-surface p-0.5 shadow-md" onMouseDown={(e) => e.stopPropagation()}>
          <span
            draggable
            onDragStart={(e) => {
              e.dataTransfer.setData(MOVE, b.id);
              e.dataTransfer.effectAllowed = 'move';
              if (ref.current) e.dataTransfer.setDragImage(ref.current, 20, 20);
            }}
            onDragEnd={onDragDone}
            title="Drag to move"
            className="flex h-7 w-8 cursor-grab items-center justify-center rounded text-text-muted hover:bg-surface-raised active:cursor-grabbing"
          >
            ⠿
          </span>
          {actions.map(([label, title, fn, disabled]) => (
            <button key={title} type="button" title={title} aria-label={title} disabled={disabled} onClick={fn} className="flex h-7 w-8 items-center justify-center rounded text-sm text-text-secondary hover:bg-surface-raised disabled:opacity-30">
              {label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function TemplatesDialog({ onClose, onApply }) {
  const [list, setList] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    fetchTemplates().then(setList).catch((e) => setErr(errMsg(e)));
  }, []);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-surface p-5 shadow-lg">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-text-primary">Start from a template</h3>
          <button onClick={onClose} className="rounded px-2 py-1 text-text-muted hover:bg-surface-raised" aria-label="Close">✕</button>
        </div>
        <p className="mt-1 text-sm text-text-muted">Replaces the email’s content and styles (you can undo).</p>
        {err && <p className="mt-2 text-sm text-error">{err}</p>}
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          {(list || []).map((t) => (
            <div key={t.id} className="flex flex-col rounded-md border border-line p-3 hover:border-accent">
              <div className="flex items-start justify-between gap-2">
                <div className="text-sm font-medium text-text-primary">{t.name}</div>
                {t.builtIn ? <span className="rounded bg-surface-raised px-1.5 text-[10px] text-text-muted">Starter</span> : null}
              </div>
              <div className="mt-0.5 flex-1 text-xs text-text-muted">{t.description || `${t.blocks.length} blocks${t.createdBy ? ` · by ${t.createdBy}` : ''}`}</div>
              <div className="mt-2 flex gap-2">
                <button onClick={() => onApply(t)} className="rounded-md bg-accent px-3 py-1 text-xs font-medium text-white">Use</button>
                {!t.builtIn && (
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Delete the template “${t.name}”?`)) return;
                      await deleteTemplate(t.id);
                      setList((l) => l.filter((x) => x.id !== t.id));
                    }}
                    className="text-xs text-text-muted hover:text-error"
                  >
                    Delete
                  </button>
                )}
              </div>
            </div>
          ))}
          {!list && !err && <p className="text-sm text-text-muted">Loading…</p>}
        </div>
      </div>
    </div>
  );
}

export default function EmailBuilder({ blocks, style, onChange, store, discountId, discountName, previewCtx, aiDraft, subject, preheader, focus }) {
  const T = useMemo(() => resolveTheme(style), [style]);
  const [sel, setSel] = useState(null);
  const [caret, setCaret] = useState(null);
  const [drop, setDrop] = useState(null);
  const [view, setView] = useState('desktop'); // desktop | phone
  const [toast, setToast] = useState('');
  const [tplOpen, setTplOpen] = useState(false);
  const [tplName, setTplName] = useState(null);
  const [brief, setBrief] = useState('');
  const [aiBusy, setAiBusy] = useState(false);
  const [brandMsg, setBrandMsg] = useState('');
  const past = useRef([]);
  const future = useRef([]);
  const lastKey = useRef({ key: null, at: 0 });
  const canvasRef = useRef(null);
  const state = useRef({ blocks, style });
  state.current = { blocks, style };

  const ctx = useMemo(
    () => ({
      appUrl: window.location.origin,
      currency: previewCtx?.currency,
      promotion: previewCtx?.promotion || null,
      prices: previewCtx?.prices,
      bestSellers: previewCtx?.bestSellers || null,
      lookbookUrl: '#lookbook',
      editing: true, // show every product picked, even ones outside the preview client's range
      now: new Date(),
    }),
    [previewCtx]
  );
  const flash = (m) => {
    setToast(m);
    setTimeout(() => setToast(''), 2500);
  };

  // --- history -----------------------------------------------------------------------------------
  const commit = useCallback(
    (patch, key = null) => {
      const now = Date.now();
      const same = key && lastKey.current.key === key && now - lastKey.current.at < 1500;
      if (!same) {
        past.current.push({ blocks: state.current.blocks, style: state.current.style });
        if (past.current.length > 100) past.current.shift();
      }
      lastKey.current = { key, at: now };
      future.current = [];
      onChange(patch);
    },
    [onChange]
  );
  const undo = () => {
    const prev = past.current.pop();
    if (!prev) return;
    future.current.push({ blocks: state.current.blocks, style: state.current.style });
    lastKey.current = { key: null, at: 0 };
    onChange(prev);
  };
  const redo = () => {
    const next = future.current.pop();
    if (!next) return;
    past.current.push({ blocks: state.current.blocks, style: state.current.style });
    onChange(next);
  };

  // --- block ops ----------------------------------------------------------------------------------
  const setBlocks = (next, key) => commit({ blocks: next }, key);
  const insertAt = (idx, b) => {
    const next = [...blocks];
    next.splice(idx, 0, b);
    setBlocks(next);
    setSel(b.id);
    setCaret(null);
  };
  const fresh = (type) => {
    const b = makeBlock(type);
    return type === 'countdown' && discountId ? { ...b, source: 'promotion' } : b;
  };
  const add = (type) => {
    const i = blocks.findIndex((b) => b.id === sel);
    insertAt(i >= 0 ? i + 1 : blocks.length, fresh(type));
  };
  // Saved sections: reusable blocks (a sign-off, a brand footer, a "how to order" panel…).
  const [sections, setSections] = useState([]);
  useEffect(() => {
    fetchTemplates('section').then(setSections).catch(() => setSections([]));
  }, []);
  const insertSection = (sec, at) => {
    const i = blocks.findIndex((b) => b.id === sel);
    const idx = at ?? (i >= 0 ? i + 1 : blocks.length);
    const made = sec.blocks.map((b) => cloneBlock(b)).filter((b) => b.type !== 'promotion' || discountId);
    const next = [...blocks];
    next.splice(idx, 0, ...made);
    setBlocks(next);
    setSel(made[0]?.id || null);
    setCaret(null);
  };
  const saveSection = async (name) => {
    const b = blocks.find((x) => x.id === sel);
    if (!b) return false;
    try {
      const t = await saveTemplate({ kind: 'section', name, blocks: [b] });
      setSections((l) => [t, ...l]);
      flash(`Saved “${name}” — find it under Saved sections.`);
      return true;
    } catch (e) {
      flash(errMsg(e));
      return false;
    }
  };
  const updateBlock = (id, nb, key) => setBlocks(blocks.map((b) => (b.id === id ? nb : b)), key);
  const remove = (id) => {
    const i = blocks.findIndex((b) => b.id === id);
    setBlocks(blocks.filter((b) => b.id !== id));
    setSel(blocks[i + 1]?.id || blocks[i - 1]?.id || null);
  };
  const duplicate = (id) => {
    const i = blocks.findIndex((b) => b.id === id);
    const copy = cloneBlock(blocks[i]);
    insertAt(i + 1, copy);
  };
  const move = (id, d) => {
    const i = blocks.findIndex((b) => b.id === id);
    const j = i + d;
    if (j < 0 || j >= blocks.length) return;
    const next = [...blocks];
    const [x] = next.splice(i, 1);
    next.splice(j, 0, x);
    setBlocks(next);
  };

  // --- drag and drop ---------------------------------------------------------------------------
  const onDrop = async (e) => {
    e.preventDefault();
    const idx = drop ?? blocks.length;
    setDrop(null);
    const files = [...(e.dataTransfer.files || [])].filter((f) => f.type.startsWith('image/'));
    if (files.length) {
      flash(`Uploading ${files.length > 1 ? `${files.length} images` : 'image'}…`);
      try {
        const made = [];
        for (const f of files) {
          const a = await uploadImageFile(f);
          made.push({ ...makeBlock('image'), url: a.url, alt: a.name.replace(/\.[a-z0-9]+$/i, '').replace(/[-_]+/g, ' ') });
        }
        const next = [...state.current.blocks];
        next.splice(idx, 0, ...made);
        commit({ blocks: next });
        setSel(made[0].id);
        flash('Image added — add a description for screen readers.');
      } catch (err) {
        flash(errMsg(err));
      }
      return;
    }
    const type = e.dataTransfer.getData(NEW);
    if (type && BLOCKS[type]) return insertAt(idx, fresh(type));
    const secId = e.dataTransfer.getData(SECTION);
    const sec = secId && sections.find((x) => x.id === secId);
    if (sec) return insertSection(sec, idx);
    const id = e.dataTransfer.getData(MOVE);
    if (id) {
      const from = blocks.findIndex((b) => b.id === id);
      if (from < 0) return;
      const next = [...blocks];
      const [x] = next.splice(from, 1);
      next.splice(from < idx ? idx - 1 : idx, 0, x);
      setBlocks(next);
    }
  };

  // --- keyboard -------------------------------------------------------------------------------------
  useEffect(() => {
    const h = (e) => {
      const mod = e.metaKey || e.ctrlKey;
      const typing = isTyping(document.activeElement);
      if (mod && e.key.toLowerCase() === 'z' && !typing) {
        e.preventDefault();
        e.shiftKey ? redo() : undo();
        return;
      }
      if (mod && e.key.toLowerCase() === 'y' && !typing) {
        e.preventDefault();
        redo();
        return;
      }
      if (e.key === 'Escape') {
        if (typing) document.activeElement.blur();
        setSel(null);
        return;
      }
      if (typing || !sel) return;
      if (e.key === 'Backspace' || e.key === 'Delete') {
        e.preventDefault();
        remove(sel);
      } else if (mod && e.key.toLowerCase() === 'd') {
        e.preventDefault();
        duplicate(sel);
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
        e.preventDefault();
        const d = e.key === 'ArrowUp' ? -1 : 1;
        if (e.altKey) move(sel, d);
        else {
          const i = blocks.findIndex((b) => b.id === sel);
          const n = blocks[i + d];
          if (n) setSel(n.id);
        }
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  });

  // Jump to a block (from the pre-send checks).
  useEffect(() => {
    if (!focus?.id) return;
    setSel(focus.id);
    setCaret(null);
    setView('desktop');
    setTimeout(() => canvasRef.current?.querySelector(`[data-block="${focus.id}"]`)?.scrollIntoView({ block: 'center', behavior: 'smooth' }), 50);
  }, [focus]);

  const selected = blocks.find((b) => b.id === sel) || null;

  const applyTemplate = (t) => {
    const fresh = (t.blocks || []).map((b) => cloneBlock(b)).filter((b) => b.type !== 'promotion' || discountId);
    commit({ blocks: fresh, style: t.style || style });
    setSel(null);
    setTplOpen(false);
    flash(`Applied “${t.name}”.`);
  };

  const runAi = async () => {
    setAiBusy(true);
    try {
      const d = await aiDraft(brief);
      const next = [...blocks];
      const hi = next.findIndex((b) => b.type === 'heading');
      const ti = next.findIndex((b) => b.type === 'text');
      if (hi >= 0 && d.heading) next[hi] = { ...next[hi], html: d.heading.replace(/</g, '&lt;'), text: undefined };
      if (ti >= 0) next[ti] = { ...next[ti], html: textToRich(d.body), text: undefined };
      else next.splice(hi + 1, 0, { ...makeBlock('text'), html: textToRich(d.body) });
      commit({ blocks: next, subject: d.subject || subject, preheader: d.preheader || preheader });
      flash('Drafted — review and edit before sending.');
    } catch (e) {
      flash(errMsg(e));
    } finally {
      setAiBusy(false);
    }
  };

  const phoneHtml = useMemo(() => (view === 'phone' ? renderCampaign({ subject, preheader, blocks, style }, { ...ctx, contactName: 'Alex Buyer', company: 'Example Store', senderName: 'Your account manager', unsubscribeUrl: '#' }).html : ''), [view, blocks, style, subject, preheader, ctx]);

  return (
    <div className="grid overflow-hidden rounded-lg border border-line bg-surface shadow-sm lg:h-[calc(100vh-150px)] lg:min-h-[640px] lg:grid-cols-[210px_minmax(0,1fr)_330px]">
      {/* palette */}
      <aside className="overflow-y-auto border-b border-line bg-surface lg:border-b-0 lg:border-r">
        {GROUPS.map((g) => (
          <div key={g} className="px-3 pt-3">
            <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-muted">{g}</div>
            <div className="grid grid-cols-3 gap-1.5 lg:grid-cols-2">
              {Object.entries(BLOCKS)
                .filter(([, d]) => d.group === g)
                .filter(([k]) => k !== 'promotion' || discountId)
                .map(([k, d]) => (
                  <button
                    key={k}
                    type="button"
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData(NEW, k);
                      e.dataTransfer.effectAllowed = 'copy';
                    }}
                    onDragEnd={() => setDrop(null)}
                    onClick={() => add(k)}
                    title={`Drag into the email, or click to add${sel ? ' below the selected block' : ''}`}
                    className="flex cursor-grab flex-col items-center gap-1 rounded-md border border-line px-1 py-2 text-[11px] text-text-secondary hover:border-accent hover:bg-accent-light hover:text-text-primary active:cursor-grabbing"
                  >
                    <span className="text-text-muted">{d.icon}</span>
                    {d.label}
                  </button>
                ))}
            </div>
          </div>
        ))}
        <div className="px-3 pt-3">
          <div className="mb-1.5 text-[11px] font-semibold uppercase tracking-wide text-text-muted">Saved sections</div>
          {sections.length ? (
            <div className="space-y-1">
              {sections.map((sec) => (
                <div key={sec.id} className="group flex items-center gap-1">
                  <button
                    type="button"
                    draggable
                    onDragStart={(e) => {
                      e.dataTransfer.setData(SECTION, sec.id);
                      e.dataTransfer.effectAllowed = 'copy';
                    }}
                    onDragEnd={() => setDrop(null)}
                    onClick={() => insertSection(sec)}
                    title="Drag into the email, or click to add"
                    className="min-w-0 flex-1 cursor-grab truncate rounded-md border border-line px-2 py-1.5 text-left text-[11px] text-text-secondary hover:border-accent hover:bg-accent-light hover:text-text-primary"
                  >
                    ☆ {sec.name}
                  </button>
                  <button
                    type="button"
                    title="Delete section"
                    onClick={async () => {
                      if (!window.confirm(`Delete the saved section “${sec.name}”? Emails already using it keep their copy.`)) return;
                      await deleteTemplate(sec.id).catch(() => {});
                      setSections((l) => l.filter((x) => x.id !== sec.id));
                    }}
                    className="hidden px-1 text-xs text-text-muted hover:text-error group-hover:block"
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-[11px] leading-snug text-text-muted">Select a block and choose “Save as a reusable section” to keep it here for every email.</p>
          )}
        </div>
        <div className="mt-3 space-y-2 border-t border-line px-3 py-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">Write with AI</div>
          <textarea value={brief} onChange={(e) => setBrief(e.target.value)} rows={2} placeholder="Optional brief, e.g. mention the new swim range" className="w-full rounded-md border border-line px-2 py-1.5 text-xs outline-none focus:border-accent" />
          <button type="button" onClick={runAi} disabled={aiBusy} className="w-full rounded-md border border-line px-2 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-raised disabled:opacity-50">
            {aiBusy ? 'Drafting…' : '✦ Draft the copy'}
          </button>
        </div>
        <div className="space-y-1.5 border-t border-line px-3 py-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-text-muted">Templates</div>
          <button type="button" onClick={() => setTplOpen(true)} className="w-full rounded-md border border-line px-2 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-raised">Start from a template…</button>
          {tplName === null ? (
            <button type="button" onClick={() => setTplName('')} className="w-full rounded-md border border-line px-2 py-1.5 text-xs font-medium text-text-secondary hover:bg-surface-raised">Save as template…</button>
          ) : (
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                if (!tplName.trim()) return;
                try {
                  await saveTemplate({ name: tplName.trim(), blocks: blocks.filter((b) => b.type !== 'promotion'), style: style || null });
                  flash('Template saved.');
                  setTplName(null);
                } catch (err) {
                  flash(errMsg(err));
                }
              }}
              className="flex gap-1"
            >
              <input autoFocus value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="Template name" className="min-w-0 flex-1 rounded border border-line px-2 py-1 text-xs" />
              <button className="rounded bg-accent px-2 text-xs text-white">Save</button>
            </form>
          )}
        </div>
      </aside>

      {/* canvas */}
      <section className="relative flex min-h-[520px] flex-col overflow-hidden" style={{ background: T.bg }}>
        <div className="flex items-center gap-2 border-b border-line bg-surface/90 px-3 py-1.5 text-xs backdrop-blur">
          <button type="button" onClick={undo} disabled={!past.current.length} title="Undo (⌘Z)" className="rounded px-2 py-1 text-text-secondary hover:bg-surface-raised disabled:opacity-30">↶ Undo</button>
          <button type="button" onClick={redo} disabled={!future.current.length} title="Redo (⇧⌘Z)" className="rounded px-2 py-1 text-text-secondary hover:bg-surface-raised disabled:opacity-30">↷ Redo</button>
          <span className="ml-1 hidden text-text-muted 2xl:inline">Drag blocks in · drop image files to upload · ⌫ deletes · ⌘D duplicates</span>
          <span className="ml-auto inline-flex rounded-md border border-line bg-surface-raised p-0.5">
            {[
              ['desktop', 'Desktop'],
              ['phone', 'Phone'],
            ].map(([k, l]) => (
              <button key={k} type="button" onClick={() => setView(k)} className={`rounded px-2.5 py-0.5 font-medium ${view === k ? 'bg-surface text-text-primary shadow-sm' : 'text-text-muted'}`}>
                {l}
              </button>
            ))}
          </span>
        </div>
        {view === 'phone' ? (
          <div className="flex flex-1 justify-center overflow-y-auto bg-surface-raised/50 p-4">
            <div className="h-fit rounded-[32px] border-[10px] border-gray-900 bg-gray-900 shadow-lg">
              <iframe title="Phone preview" srcDoc={phoneHtml} sandbox="" className="h-[720px] w-[375px] rounded-[22px] bg-white" />
            </div>
          </div>
        ) : (
          <div
            ref={canvasRef}
            className="flex-1 overflow-y-auto px-14 py-8"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) setSel(null);
            }}
            onDragOver={(e) => {
              e.preventDefault();
              if (e.target === e.currentTarget || !blocks.length) setDrop(blocks.length);
            }}
            onDragLeave={(e) => {
              if (!e.currentTarget.contains(e.relatedTarget)) setDrop(null);
            }}
            onDrop={onDrop}
          >
            <div className="mx-auto" style={{ maxWidth: T.width }}>
              {T.header.show && (
                <div
                  className="cursor-pointer rounded hover:outline hover:outline-1 hover:outline-dashed hover:outline-accent/60"
                  title="Edit the header in Email styles"
                  onMouseDown={() => setSel(null)}
                  dangerouslySetInnerHTML={{ __html: `<table role="presentation" width="100%" cellpadding="0" cellspacing="0">${headerHtml(T, ctx)}</table>` }}
                />
              )}
              <div
                className="relative"
                style={{ background: T.contentBg, border: T.border ? `1px solid ${T.line}` : 'none', borderRadius: T.radius, padding: '22px 0', '--t8-link': T.link }}
                onMouseDown={(e) => {
                  if (e.target === e.currentTarget) setSel(null);
                }}
              >
                {blocks.map((b, i) => (
                  <div key={b.id}>
                    {drop === i && <div className="mx-4 my-0.5 h-1 rounded-full bg-accent" />}
                    <CanvasBlock
                      b={b}
                      i={i}
                      T={T}
                      ctx={ctx}
                      selected={sel === b.id}
                      caret={sel === b.id ? caret : null}
                      onSelect={(id, pt) => {
                        setSel(id);
                        setCaret(pt);
                      }}
                      onEditHtml={(html) => updateBlock(b.id, { ...b, html, text: undefined }, `edit:${b.id}`)}
                      onDragOverIdx={setDrop}
                      onDragDone={() => setDrop(null)}
                      actions={[
                        ['↑', 'Move up', () => move(b.id, -1), i === 0],
                        ['↓', 'Move down', () => move(b.id, 1), i === blocks.length - 1],
                        ['⧉', 'Duplicate', () => duplicate(b.id), false],
                        ['✕', 'Delete', () => remove(b.id), false],
                      ]}
                    />
                  </div>
                ))}
                {drop === blocks.length && blocks.length > 0 && <div className="mx-4 my-0.5 h-1 rounded-full bg-accent" />}
                {!blocks.length && (
                  <div className={`mx-8 my-6 rounded-lg border-2 border-dashed px-6 py-14 text-center text-sm ${drop !== null ? 'border-accent bg-accent-light text-text-primary' : 'border-line-strong text-text-muted'}`}>
                    Drag blocks here — or click one on the left — to start building.
                  </div>
                )}
              </div>
              <div className="px-2 pt-4 text-[12px] leading-relaxed" style={{ color: T.muted, fontFamily: (FONTS[T.font] || FONTS.system).stack }}>
                {T.footerNote && <div>{T.footerNote}</div>}
                You’re receiving this because {'{company}'} is a TEAMM8 wholesale customer. Replies go straight to {'{senderName}'}.<br />
                TEAMM8 Wholesale · Sydney, Australia · <span className="underline">Unsubscribe from marketing emails</span>
              </div>
            </div>
          </div>
        )}
        {toast && <div className="pointer-events-none absolute bottom-4 left-1/2 -translate-x-1/2 rounded-md bg-gray-900 px-3 py-2 text-xs text-white shadow-lg">{toast}</div>}
      </section>

      {/* settings */}
      <aside className="overflow-y-auto border-t border-line bg-surface lg:border-l lg:border-t-0">
        {selected ? (
          <BlockInspector
            key={selected.id}
            b={selected}
            store={store}
            discountName={discountName}
            onChange={(nb) => updateBlock(selected.id, nb, `inspect:${selected.id}`)}
            onDuplicate={() => duplicate(selected.id)}
            onDelete={() => remove(selected.id)}
            onSaveSection={saveSection}
          />
        ) : (
          <ThemePanel
            style={style}
            store={store}
            onChange={(s) => commit({ style: s }, 'theme')}
            brandMsg={brandMsg}
            onSaveBrand={async () => {
              try {
                await saveBrandTheme(style || {});
                setBrandMsg('Saved — new emails start with this style.');
              } catch (e) {
                setBrandMsg(errMsg(e));
              }
              setTimeout(() => setBrandMsg(''), 3000);
            }}
          />
        )}
      </aside>
      {tplOpen && <TemplatesDialog onClose={() => setTplOpen(false)} onApply={applyTemplate} />}
    </div>
  );
}
